import React, { useEffect, useState, useContext } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";

import axios from "axios";
import Swal from "sweetalert2";

import "./subcategories.css";

import Header from "../../components/header/Header";
import Subscribe from "../../components/subscribe/Subscribe";
import Footer from "../../components/footer/Footer";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import StarIcon from "@mui/icons-material/Star";

import IconOne from "../../assets/images/category-1.svg";
import Watermark from "../../assets/images/no_product.jpg";

import { WishlistContext } from "../../context/WishlistContext";
import { CartContext } from "../../context/CartContext";

import { baseURL } from "../../constants/url";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Subcategory() {
  const { id } = useParams();

  const query = useQuery();

  const navigate = useNavigate();

  const subCategoryId =
    query.get("sub_category");

  // ----------------------------------------------------
  // State
  // ----------------------------------------------------

  const [categories, setCategories] =
    useState([]);

  const [products, setProducts] =
    useState([]);

  const [
    selectedCategoryId,
    setSelectedCategoryId,
  ] = useState(null);

  const [loading, setLoading] =
    useState(false);

  const [
    loadMoreLoading,
    setLoadMoreLoading,
  ] = useState(false);

  const [nextUrl, setNextUrl] =
    useState(null);

  // ----------------------------------------------------
  // Context
  // ----------------------------------------------------

  const {
    fetchWishlistCount,
  } = useContext(WishlistContext);

  const {
    cartItems,
    fetchCartCount,
  } = useContext(CartContext);

  // ----------------------------------------------------
  // Fetch Categories
  // ----------------------------------------------------

  const fetchCategories =
    async () => {
      try {
        const res =
          await axios.get(
            `${baseURL}common/product_categories/`
          );

        setCategories(
          res.data || []
        );
      } catch (error) {
        console.error(
          "Error loading categories:",
          error
        );
      }
    };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ----------------------------------------------------
  // Fetch Wishlist
  // ----------------------------------------------------

  const fetchWishlistProducts =
    async () => {
      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        return [];
      }

      try {
        const res =
          await axios.get(
            `${baseURL}common/wishlist`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        return (
          res.data.results ||
          []
        );
      } catch (error) {
        console.error(
          "Error fetching wishlist:",
          error
        );

        return [];
      }
    };

  // ----------------------------------------------------
  // Mark wishlist in product list
  // ----------------------------------------------------

  const applyWishlistStatus =
    async (productList) => {
      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        return productList;
      }

      try {
        const wishlistItems =
          await fetchWishlistProducts();

        const wishlistedVariantIds =
          new Set();

        wishlistItems.forEach(
          (item) => {
            // Support different wishlist response shapes
            if (
              item?.variant?.id
            ) {
              wishlistedVariantIds.add(
                item.variant.id
              );
            }

            if (
              item?.variant_id
            ) {
              wishlistedVariantIds.add(
                item.variant_id
              );
            }

            if (
              item?.product
                ?.variants
            ) {
              item.product.variants.forEach(
                (variant) => {
                  wishlistedVariantIds.add(
                    variant.id
                  );
                }
              );
            }
          }
        );

        return productList.map(
          (product) => ({
            ...product,

            variants:
              product.variants?.map(
                (variant) => ({
                  ...variant,

                  is_wishlisted:
                    variant.is_wishlisted ||
                    wishlistedVariantIds.has(
                      variant.id
                    ),
                })
              ) || [],
          })
        );
      } catch (error) {
        console.error(
          "Error applying wishlist:",
          error
        );

        return productList;
      }
    };

  // ----------------------------------------------------
  // Fetch First 50 Products
  // ----------------------------------------------------

  const fetchProductsAndWishlist =
    async (
      categoryId,
      subCatId
    ) => {
      setLoading(true);

      setProducts([]);

      setNextUrl(null);

      try {
        let url =
          `${baseURL}common/products/?category=${categoryId}`;

        if (subCatId) {
          url +=
            `&sub_category=${subCatId}`;
        }

        const productRes =
          await axios.get(url);

        const responseData =
          productRes.data;

        let fetchedProducts =
          responseData?.results ||
          (Array.isArray(
            responseData
          )
            ? responseData
            : []);

        fetchedProducts =
          await applyWishlistStatus(
            fetchedProducts
          );

        setProducts(
          fetchedProducts
        );

        setNextUrl(
          responseData?.next ||
            null
        );

        console.log(
          "First Page Products:",
          fetchedProducts.length
        );

        console.log(
          "Total Products:",
          responseData?.count
        );

        console.log(
          "Next URL:",
          responseData?.next
        );
      } catch (error) {
        console.error(
          "Error loading products or wishlist:",
          error
        );

        setProducts([]);

        setNextUrl(null);
      } finally {
        setLoading(false);
      }
    };

  // ----------------------------------------------------
  // Category / Subcategory Change
  // ----------------------------------------------------

  useEffect(() => {
    if (id) {
      setSelectedCategoryId(
        Number(id)
      );

      fetchProductsAndWishlist(
        id,
        subCategoryId
      );
    }
  }, [id, subCategoryId]);

  // ----------------------------------------------------
  // Load More - Next 50 Products
  // ----------------------------------------------------

  const loadMoreProducts =
    async () => {
      if (!nextUrl) {
        return;
      }

      if (loadMoreLoading) {
        return;
      }

      setLoadMoreLoading(true);

      try {
        const productRes =
          await axios.get(
            nextUrl
          );

        const responseData =
          productRes.data;

        let newProducts =
          responseData?.results ||
          (Array.isArray(
            responseData
          )
            ? responseData
            : []);

        // Apply wishlist status on new page too
        newProducts =
          await applyWishlistStatus(
            newProducts
          );

        setProducts(
          (prevProducts) => [
            ...prevProducts,
            ...newProducts,
          ]
        );

        setNextUrl(
          responseData?.next ||
            null
        );

        console.log(
          "New Products Loaded:",
          newProducts.length
        );

        console.log(
          "Next URL:",
          responseData?.next
        );
      } catch (error) {
        console.error(
          "Error loading more products:",
          error
        );

        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load more products.",
        });
      } finally {
        setLoadMoreLoading(
          false
        );
      }
    };

  // ----------------------------------------------------
  // Wishlist Toggle
  // ----------------------------------------------------

  const toggleWishlist =
    async (variantId) => {
      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        Swal.fire({
          icon: "error",
          title:
            "Please Login",
          text: "Please log in to use wishlist.",
          confirmButtonText:
            "Go to Login",
        }).then(() => {
          navigate(
            "/login"
          );
        });

        return;
      }

      try {
        const response =
          await axios.post(
            `${baseURL}common/wishlist_toggle/${variantId}/`,
            {},
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const isWishlisted =
          response?.data
            ?.message
            ?.toLowerCase()
            .includes(
              "added"
            );

        Swal.fire({
          icon: isWishlisted
            ? "success"
            : "info",

          title:
            isWishlisted
              ? "Added to Wishlist"
              : "Removed from Wishlist",

          toast: true,

          position:
            "top-end",

          timer: 2000,

          showConfirmButton:
            false,
        });

        setProducts(
          (prevProducts) =>
            prevProducts.map(
              (product) => ({
                ...product,

                variants:
                  product.variants?.map(
                    (
                      variant
                    ) =>
                      variant.id ===
                      variantId
                        ? {
                            ...variant,

                            is_wishlisted:
                              isWishlisted,
                          }
                        : variant
                  ) || [],
              })
            )
        );

        fetchWishlistCount();
      } catch (error) {
        console.error(
          "Error toggling wishlist:",
          error
        );

        Swal.fire({
          icon: "error",
          title:
            "Something went wrong!",
          text: "Please try again later.",
          toast: true,
          position:
            "top-end",
          timer: 3000,
          showConfirmButton:
            false,
        });
      }
    };

  // ----------------------------------------------------
  // Add To Cart
  // ----------------------------------------------------

  const addToCart =
    async (variant) => {
      if (!variant) {
        return;
      }

      if (
        Number(
          variant.stock
        ) === 0
      ) {
        return;
      }

      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        Swal.fire({
          icon: "warning",
          title:
            "Login Required",
          text: "Please login first",
          confirmButtonText:
            "Go to Login",
        }).then(() => {
          navigate(
            "/login"
          );
        });

        return;
      }

      const alreadyInCart =
        cartItems?.some(
          (item) =>
            item.variant_id ===
              variant.id ||
            item.variant_id?.id ===
              variant.id
        );

      if (alreadyInCart) {
        Swal.fire({
          icon: "info",
          title:
            "Already in Cart",
          text: "This product is already in your cart.",
          timer: 1500,
          showConfirmButton:
            false,
        });

        return;
      }

      try {
        await axios.post(
          `${baseURL}common/cart/add/`,

          {
            variant_id:
              variant.id,

            quantity: 1,
          },

          {
            headers: {
              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },
          }
        );

        Swal.fire({
          icon: "success",
          title:
            "Added to Cart!",
          text: "The product has been added to your cart.",
          timer: 1500,
          showConfirmButton:
            false,
        });

        fetchCartCount();
      } catch (error) {
        console.error(
          "Cart Error:",
          error?.response
            ?.data ||
            error.message
        );

        Swal.fire({
          icon: "error",
          title: "Error",

          text:
            error?.response
              ?.data?.message ||
            error?.response
              ?.data?.detail ||
            "Failed to add product to cart.",
        });
      }
    };

  // ----------------------------------------------------
  // Category Click
  // ----------------------------------------------------

  const handleCategoryClick =
    (
      categoryId,
      subCatId = null
    ) => {
      let url =
        `/subcategories/${categoryId}`;

      if (subCatId) {
        url +=
          `?sub_category=${subCatId}`;
      }

      navigate(url);
    };

  // ----------------------------------------------------
  // UI
  // ----------------------------------------------------

  return (
    <div>
      <Header />

      <div className="container-fluid">
        <div className="row">

          <div className="col-lg-12 pt_20 mb_30">

            <div className="prod_list_grid">

              {/* Category List */}

              <div className="category-box">

                <h4>
                  Categories
                </h4>

                {categories.map(
                  (cat) => (

                    <div
                      key={
                        cat.id
                      }
                      className={`category-item ${
                        selectedCategoryId ===
                        cat.id
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        handleCategoryClick(
                          cat.id
                        )
                      }
                      style={{
                        cursor:
                          "pointer",
                      }}
                    >

                      <div className="category-left">

                        <img
                          src={
                            cat.image ||
                            IconOne
                          }
                          alt={
                            cat.name
                          }
                          onError={(
                            e
                          ) => {
                            e.currentTarget.src =
                              IconOne;
                          }}
                        />

                        <span>
                          {
                            cat.name
                          }
                        </span>

                      </div>

                    </div>
                  )
                )}

              </div>

              {/* Products */}

              <div className="popular_products subcategory">

                <div className="popular_products_grid">

                  {loading ? (

                    <div className="container-fluid text-center pt_40 pb_40">

                      <p>
                        Loading...
                      </p>

                    </div>

                  ) : products?.length >
                    0 ? (

                    products.map(
                      (
                        product
                      ) => {
                        const variant =
                          product
                            .variants?.[0];

                        if (
                          !variant
                        ) {
                          return null;
                        }

                        const isOutOfStock =
                          Number(
                            variant.stock
                          ) === 0;

                        const imagePath =
                          variant
                            ?.images?.[0]
                            ?.image;

                        return (

                          <div
                            key={
                              variant.id
                            }
                            className="popular_products_grid_in"
                          >

                            {/* Header */}

                            <div className="products_head">

                              <div className="whislist_icon_box d_flex justify_sp_between">

                                {/* Wishlist */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleWishlist(
                                      variant.id
                                    )
                                  }
                                  className="wishlist-btn"
                                  style={{
                                    background:
                                      "none",

                                    border:
                                      "none",

                                    cursor:
                                      "pointer",
                                  }}
                                >

                                  {variant.is_wishlisted ? (

                                    <FavoriteIcon
                                      style={{
                                        color:
                                          "red",
                                      }}
                                    />

                                  ) : (

                                    <FavoriteBorderIcon
                                      style={{
                                        color:
                                          "green",
                                      }}
                                    />

                                  )}

                                </button>

                                {/* Cart */}

                                <button
                                  type="button"
                                  onClick={() =>
                                    addToCart(
                                      variant
                                    )
                                  }
                                  disabled={
                                    isOutOfStock
                                  }
                                  className="add_to_cart_icons_N mt_5 wishlist-btn font_size14"
                                  style={{
                                    background:
                                      "transparent",

                                    border:
                                      "none",

                                    cursor:
                                      isOutOfStock
                                        ? "not-allowed"
                                        : "pointer",

                                    opacity:
                                      isOutOfStock
                                        ? 0.4
                                        : 1,
                                  }}
                                >

                                  <AddShoppingCartIcon
                                    style={{
                                      color:
                                        "green",
                                    }}
                                  />

                                </button>

                              </div>

                            </div>

                            {/* Product Details */}

                            <div
                              className={`products_details ${
                                isOutOfStock
                                  ? "out-of-stock"
                                  : ""
                              }`}
                            >

                              {/* Image */}

                              <div className="products_img d_flex align_center">

                                <Link
                                  to={`/product-details/${product.id}`}
                                >

                                  <img
                                    src={
                                      imagePath ||
                                      IconOne
                                    }
                                    alt={
                                      product.name
                                    }
                                    onError={(
                                      e
                                    ) => {
                                      e.currentTarget.src =
                                        IconOne;
                                    }}
                                  />

                                </Link>

                                {isOutOfStock && (

                                  <span className="out_of_stock_badge">
                                    Out of Stock
                                  </span>

                                )}

                              </div>

                              {/* Info */}

                              <div className="products_info pt_10">

                                <div className="products_name mt_5">

                                  <Link
                                    to={`/product-details/${product.id}`}
                                  >
                                    {
                                      product.name
                                    }
                                  </Link>

                                </div>

                                {/* Price */}

                                <div className="pro_price mt_5 d_flex gap_10 align_center">

                                  <h6>
                                    ₹
                                    {
                                      variant.final_price
                                    }
                                    /-
                                  </h6>

                                  <h6 className="price_cut">

                                    <s>
                                      ₹
                                      {
                                        variant.price
                                      }
                                      /-
                                    </s>

                                  </h6>

                                  <h6 className="common_color">

                                    (
                                    {
                                      variant.discount_percent
                                    }
                                    % Off)

                                  </h6>

                                </div>

                                {/* Rating */}

                                <div className="rating_part d_flex align_center gap_5">

                                  <span>
                                    {variant.average_rating ||
                                      "0.0"}
                                  </span>

                                  <StarIcon />

                                </div>

                                {/* Buy Button */}

                                <div className="products_footer d_flex align_center justify_sp_center">

                                  {isOutOfStock ? (

                                    <span
                                      className="buy_btn font_size14 disabled-link"
                                      style={{
                                        background:
                                          "#f38c55",

                                        cursor:
                                          "not-allowed",
                                      }}
                                    >
                                      Out of Stock
                                    </span>

                                  ) : (

                                    <Link
                                      to={`/product-details/${product.id}`}
                                      className="buy_btn font_size14"
                                    >
                                      Buy Now
                                    </Link>

                                  )}

                                </div>

                              </div>

                            </div>

                          </div>
                        );
                      }
                    )

                  ) : (

                    <div className="container-fluid text-center pt_40 pb_40">

                      <img
                        src={
                          Watermark
                        }
                        alt="No Products Found"
                        className="no-products-img"
                        style={{
                          width:
                            "300px",

                          opacity:
                            0.3,
                        }}
                      />

                      <h4 className="pt_20">
                        No Products Available
                      </h4>

                    </div>
                  )}

                </div>

                {/* Load More */}

                {!loading &&
                  nextUrl && (

                    <div className="text-center mt_30 align_center d_flex justify_sp_center">

                      <button
                        type="button"
                        className="load_more d_flex align_center gap_30"
                        onClick={
                          loadMoreProducts
                        }
                        disabled={
                          loadMoreLoading
                        }
                      >

                        {loadMoreLoading
                          ? "Loading..."
                          : "Load More"}

                        {loadMoreLoading && (
                          <span className="loader"></span>
                        )}

                      </button>

                    </div>

                  )}

              </div>

            </div>

          </div>

        </div>
      </div>

      <Subscribe />

      <Footer />
    </div>
  );
}

export default Subcategory;